/**
 * The experts of an asset: the chosen people (name, e-mail, an optional label such as "Product owner", Remove) and a
 * lookup by name or e-mail that asks the server once typing pauses, from two characters. With `user.provision`,
 * **Add a person who is not in the app** opens the provision dialog.
 */

import { useState } from 'react';
import { Search, UserPlus, X } from 'lucide-react';

import { expertInLabelOneMax, lookupUsersApiUsersLookupGetParamsQMin } from '@/api/generated/schemas';
import { useUserLookup } from '@/api/queries';
import type { UserLookupRow } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

import type { ExpertDraft } from './assetFields';
import { ProvisionDialog } from './ProvisionDialog';

function LookupResults({ q, chosen, onAdd }: { q: string; chosen: Set<string>; onAdd: (row: UserLookupRow) => void }) {
  const lookup = useUserLookup(q);
  if (q.length < lookupUsersApiUsersLookupGetParamsQMin) return null;
  if (lookup.isPending) return <p className="hint" role="status">Searching people</p>;
  if (lookup.error) return <p className="hint" role="status">The search failed. Try again.</p>;
  const rows = lookup.data.users.filter((u) => !chosen.has(u.id));
  if (rows.length === 0) return <p className="hint" role="status">No one matches. Check the spelling, or add the person below.</p>;
  return (
    <ul className="expert-results" aria-label="People found">
      {rows.map((u) => (
        <li key={u.id}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onAdd(u)}>
            <UserPlus aria-hidden="true" /> {u.display_name} <span className="hint">{u.email}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ExpertRow({ expert, onLabel, onRemove }: { expert: ExpertDraft; onLabel: (label: string) => void; onRemove: () => void }) {
  return (
    <li className="expert-row">
      <span className="expert-who">
        <b>{expert.display_name}</b> <span className="hint">{expert.email}</span>
        {expert.note && <span className="hint"> ({expert.note})</span>}
      </span>
      <input
        type="text"
        maxLength={expertInLabelOneMax}
        value={expert.label}
        placeholder="Role, for example Product owner"
        aria-label={`Role of ${expert.display_name}`}
        onChange={(e) => onLabel(e.target.value)}
      />
      <button type="button" className="icon-btn" aria-label={`Remove ${expert.display_name}`} title="Remove" onClick={onRemove}>
        <X aria-hidden="true" />
      </button>
    </li>
  );
}

export function ExpertPicker({ experts, onChange }: { experts: ExpertDraft[]; onChange: (experts: ExpertDraft[]) => void }) {
  const { can } = useSession();
  const [q, setQ] = useState('');
  const [text, setText] = useDebouncedSearch(q, (patch) => setQ(patch.q));
  const [provisioning, setProvisioning] = useState(false);
  const chosen = new Set(experts.map((e) => e.user_id));

  const add = (row: UserLookupRow, note?: string | null) => {
    onChange([...experts, { user_id: row.id, display_name: row.display_name, email: row.email, label: '', note }]);
    setText('');
  };
  const patch = (index: number, next: Partial<ExpertDraft>) =>
    onChange(experts.map((e, i) => (i === index ? { ...e, ...next } : e)));

  return (
    <fieldset className="field-group expert-picker">
      <legend>Experts</legend>
      <p className="hint">Readers see each expert's name and e-mail. Publishing needs at least one.</p>
      {experts.length > 0 ? (
        <ul className="expert-list" aria-label="Chosen experts">
          {experts.map((e, i) => (
            <ExpertRow key={e.user_id} expert={e} onLabel={(label) => patch(i, { label })} onRemove={() => onChange(experts.filter((_, j) => j !== i))} />
          ))}
        </ul>
      ) : (
        <p className="hint">No experts yet.</p>
      )}
      <div className="search-field">
        <Search aria-hidden="true" />
        <input type="search" value={text} placeholder="Find a person by name or e-mail" aria-label="Find an expert" onChange={(e) => setText(e.target.value)} />
      </div>
      <div aria-live="polite">
        <LookupResults q={q} chosen={chosen} onAdd={(row) => add(row)} />
      </div>
      {can('user.provision') && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setProvisioning(true)}>
          <UserPlus aria-hidden="true" /> Add a person who is not in the app
        </button>
      )}
      {provisioning && (
        <ProvisionDialog
          onClose={() => setProvisioning(false)}
          onProvisioned={(user, note) => {
            if (!chosen.has(user.id)) add(user, note);
            setProvisioning(false);
          }}
        />
      )}
    </fieldset>
  );
}
